import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { onIdTokenChanged, getIdToken } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../../Services/Firebase/FirebaseIndex";
import debug from '../../utils/debug';

const AuthContext = createContext();

// Storage keys
const TOKEN_STORAGE_KEY = 'gigify_auth_token';
const USER_TYPE_STORAGE_KEY = 'gigify_user_type';
const TOKEN_EXPIRY_KEY = 'gigify_token_expiry';
const LAST_REFRESH_KEY = 'gigify_last_token_refresh';
const CACHE_VERSION_KEY = 'gigify_cache_version';

// Time constants with improved granularity
const USER_TYPE_TTL = 1 * 60 * 60 * 1000; // 1 hour for user type (can change)
const TOKEN_TTL = 12 * 60 * 60 * 1000; // 12 hours for tokens
const TOKEN_REFRESH_THRESHOLD = 30 * 60 * 1000; // 30 minutes before expiry
const TOKEN_REFRESH_INTERVAL = 60 * 60 * 1000; // Refresh once per hour
const MIN_REFRESH_INTERVAL = 60 * 1000; // Minimum 1 minute between refreshes

// Cache versioning for immediate invalidation when needed
const CACHE_VERSION = 'v2';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

const getCachedAuthData = () => {
  try {
    // Check cache version first
    const cacheVersion = sessionStorage.getItem(CACHE_VERSION_KEY);
    if (cacheVersion !== CACHE_VERSION) {
      debug.log('AuthContext', 'Cache version mismatch, clearing cache');
      clearCache();
      return { token: null, userType: null, isValid: false, expiry: 0 };
    }

    const token = sessionStorage.getItem(TOKEN_STORAGE_KEY);
    const userType = sessionStorage.getItem(USER_TYPE_STORAGE_KEY);
    const expiryString = sessionStorage.getItem(TOKEN_EXPIRY_KEY);
    const expiry = expiryString ? parseInt(expiryString, 10) : 0;
    
    if (token && expiry && Date.now() < expiry) {
      debug.log('AuthContext', 'Using cached auth token', { 
        userType, 
        expiresIn: Math.round((expiry - Date.now()) / 1000) + ' seconds' 
      });
      return { token, userType, isValid: true, expiry };
    }
    
    debug.log('AuthContext', 'Cached token invalid or expired');
    return { token: null, userType: null, isValid: false, expiry: 0 };
  } catch (error) {
    debug.error('AuthContext', 'Error accessing cached auth data', error);
    return { token: null, userType: null, isValid: false, expiry: 0 };
  }
};

const clearCache = () => {
  try {
    sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    sessionStorage.removeItem(USER_TYPE_STORAGE_KEY);
    sessionStorage.removeItem(TOKEN_EXPIRY_KEY);
    sessionStorage.removeItem(LAST_REFRESH_KEY);
    sessionStorage.removeItem(CACHE_VERSION_KEY);
    debug.log('AuthContext', 'Cache cleared');
  } catch (error) {
    debug.error('AuthContext', 'Error clearing cache', error);
  }
};

const cacheAuthData = (token, userType) => {
  try {
    if (!token) {
      clearCache();
      return;
    }
    
    const expiry = Date.now() + TOKEN_TTL;
    sessionStorage.setItem(TOKEN_STORAGE_KEY, token);
    sessionStorage.setItem(USER_TYPE_STORAGE_KEY, userType || '');
    sessionStorage.setItem(TOKEN_EXPIRY_KEY, expiry.toString());
    sessionStorage.setItem(CACHE_VERSION_KEY, CACHE_VERSION);
    
    debug.log('AuthContext', 'Cached auth token', { 
      userType, 
      expiresIn: Math.round(TOKEN_TTL / 1000) + ' seconds' 
    });
  } catch (error) {
    debug.error('AuthContext', 'Error caching auth data', error);
  }
};

// Record last refresh time in session storage
const setLastRefreshTime = () => {
  try {
    sessionStorage.setItem(LAST_REFRESH_KEY, Date.now().toString());
  } catch (error) {
    debug.error('AuthContext', 'Error setting last refresh time', error);
  }
};

// Get last refresh time from session storage
const getLastRefreshTime = () => {
  try {
    const timeStr = sessionStorage.getItem(LAST_REFRESH_KEY);
    return timeStr ? parseInt(timeStr, 10) : 0;
  } catch (error) {
    debug.error('AuthContext', 'Error getting last refresh time', error);
    return 0;
  }
};

export const AuthProvider = ({ children }) => {
  // Optimize initial state loading - start with cached data to prevent blank screens
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(() => {
    const { isValid } = getCachedAuthData();
    return !isValid; // Start with false if we have valid cache
  });
  const [isVerified, setIsVerified] = useState(false);
  const [userType, setUserType] = useState(() => {
    const { userType: cachedUserType, isValid } = getCachedAuthData();
    return isValid ? cachedUserType : null;
  });
  const [authInitialized, setAuthInitialized] = useState(false);
  const [navigationReady, setNavigationReady] = useState(false);
  const [authToken, setAuthToken] = useState(() => {
    const { token, isValid } = getCachedAuthData();
    return isValid ? token : null;
  });

  // Check if token actually needs refreshing
  const shouldRefreshToken = useCallback(() => {
    const { expiry, isValid } = getCachedAuthData();
    if (!isValid) return true;

    const timeToExpiry = expiry - Date.now();
    return timeToExpiry < TOKEN_REFRESH_THRESHOLD;
  }, []);

  // Optimized user type fetching with batched Firestore calls
  const fetchUserTypeOptimized = useCallback(async (userUID) => {
    debug.log('AuthContext', 'Fetching user type with batched calls', { 
      userUID,
      authUID: currentUser?.uid,
      userIDsMatch: currentUser?.uid === userUID,
      adminUsersPath: `doc(db, "AdminUsers", "${userUID}")`,
      usersPath: `doc(db, "Users", "${userUID}")`
    });
    
    try {
      // Use batched reads instead of sequential calls
      debug.firebase('AuthContext', 'Starting parallel document reads', {
        adminUsersCollection: 'AdminUsers',
        usersCollection: 'Users',
        targetUserUID: userUID
      });
      
      const [adminDoc, userDoc] = await Promise.all([
        getDoc(doc(db, "AdminUsers", userUID)),
        getDoc(doc(db, "Users", userUID))
      ]);
      
      debug.firebase('AuthContext', 'Batch read results', {
        adminDocExists: adminDoc.exists(),
        userDocExists: userDoc.exists(),
        adminDocId: adminDoc.id,
        userDocId: userDoc.id,
        userUID
      });

      if (adminDoc.exists() && adminDoc.data().status === "active") {
        debug.log('AuthContext', 'User identified as Admin', {
          adminData: adminDoc.data(),
          status: adminDoc.data().status
        });
        return 'Admin';
      }

      if (userDoc.exists() && userDoc.data().profile?.userType) {
        const type = userDoc.data().profile.userType;
        debug.log('AuthContext', 'User type found', { 
          userType: type,
          profileData: userDoc.data().profile
        });
        return type;
      }

      debug.warn('AuthContext', 'No user type found in either collection', {
        adminDocExists: adminDoc.exists(),
        userDocExists: userDoc.exists(),
        adminStatus: adminDoc.exists() ? adminDoc.data()?.status : 'N/A',
        userProfile: userDoc.exists() ? !!userDoc.data()?.profile : false,
        userProfileType: userDoc.exists() ? userDoc.data()?.profile?.userType : 'N/A'
      });
      return null;
    } catch (error) {
      debug.error('AuthContext', 'Error fetching user type', {
        error: error.message,
        errorCode: error.code,
        errorDetails: error.details,
        userUID,
        authUID: currentUser?.uid,
        adminUsersPath: `AdminUsers/${userUID}`,
        usersPath: `Users/${userUID}`
      });
      return null;
    }
  }, []);

  // Prefetch essential user data
  const prefetchUserData = useCallback(async (userUID, userType) => {
    if (!userUID || !userType) return;
    
    debug.log('AuthContext', 'Prefetching user data', { userUID, userType });
    
    const promises = [];
    
    if (userType === 'Talent') {
      promises.push(getDoc(doc(db, 'TalentProfiles', userUID)));
    } else if (userType === 'Organiser') {
      promises.push(getDoc(doc(db, 'OrganiserProfiles', userUID)));
    }
    
    // Prefetch but don't wait for completion - fire and forget
    Promise.all(promises).catch(error => {
      debug.warn('AuthContext', 'Error prefetching user data', error);
    });
  }, []);

  const refreshToken = useCallback(async (forceRefresh = true) => {
    if (!currentUser) return null;
    
    // Only refresh if token actually needs refreshing
    if (!forceRefresh && !shouldRefreshToken()) {
      const { token } = getCachedAuthData();
      debug.log('AuthContext', 'Token refresh skipped - not needed yet');
      return token;
    }
    
    // Rate limiting check
    const now = Date.now();
    const lastRefreshTime = getLastRefreshTime();
    
    if (forceRefresh && now - lastRefreshTime < MIN_REFRESH_INTERVAL) {
      debug.log('AuthContext', 'Token refresh skipped due to rate limiting', {
        timeSinceLastRefresh: Math.round((now - lastRefreshTime) / 1000) + 's'
      });
      
      // Return the cached token if available
      const { token, isValid } = getCachedAuthData();
      if (isValid && token) return token;
      
      // Only force refresh if no valid token exists in cache
    }
    
    try {
      debug.log('AuthContext', 'Refreshing authentication token');
      const token = await getIdToken(currentUser, forceRefresh);
      setAuthToken(token);
      setLastRefreshTime();
      
      cacheAuthData(token, userType);
      
      return token;
    } catch (error) {
      if (error.code === 'auth/quota-exceeded') {
        debug.warn('AuthContext', 'Auth quota exceeded, using cached token if available');
        const { token, isValid } = getCachedAuthData();
        if (isValid && token) return token;
      }
      
      debug.error('AuthContext', 'Error refreshing token', error);
      return null;
    }
  }, [currentUser, userType]);

  const checkAndRefreshToken = useCallback(async () => {
    if (!currentUser) return;
    
    // Rate limiting check
    const now = Date.now();
    const lastRefreshTime = getLastRefreshTime();
    
    if (now - lastRefreshTime < MIN_REFRESH_INTERVAL) {
      debug.log('AuthContext', 'Token refresh rate limited', {
        timeSinceLastRefresh: Math.round((now - lastRefreshTime) / 1000) + 's',
        minInterval: MIN_REFRESH_INTERVAL / 1000 + 's'
      });
      return;
    }
    
    const { token, expiry, isValid } = getCachedAuthData();
    if (isValid) {
      if (Date.now() > expiry - TOKEN_REFRESH_THRESHOLD) {
        debug.log('AuthContext', 'Token close to expiry, refreshing');
        await refreshToken();
      }
    } else {
      await refreshToken();
    }
  }, [currentUser, refreshToken]);

  useEffect(() => {
    const initFromCache = () => {
      const { token, userType: cachedUserType, isValid } = getCachedAuthData();
      
      if (isValid && token) {
        debug.log('AuthContext', 'Initializing auth state from cache');
        setAuthToken(token);
        setUserType(cachedUserType);
      }
    };
    
    initFromCache();
  }, []);

  const initializeAuth = () => {
    debug.log('AuthContext', 'Auth initialization was requested - using existing listener');
    return Promise.resolve();
  };

  const fetchUserTypeWithRetry = async (userUID, maxRetries = 5, delayMs = 800) => {
    if (!userUID) {
      debug.warn('AuthContext', 'No userUID provided to fetchUserTypeWithRetry');
      return null;
    }
  
    const { userType: cachedUserType, isValid } = getCachedAuthData();
    if (isValid && cachedUserType) {
      debug.log('AuthContext', 'Using cached user type', { userType: cachedUserType });
      return cachedUserType;
    }
    
    let retries = maxRetries;
    
    while (retries > 0) {
      try {
        debug.firebase('AuthContext', `Fetching user type for ${userUID} (optimized batch)`, {
          attempt: maxRetries - retries + 1,
          maxRetries: maxRetries
        });
        
        // Use optimized batch call instead of sequential calls
        const fetchedUserType = await fetchUserTypeOptimized(userUID);
        
        if (fetchedUserType) {
          debug.firebase('AuthContext', 'User type found via batch call', {
            userUID,
            userType: fetchedUserType
          });
          
          if (authToken) {
            cacheAuthData(authToken, fetchedUserType);
          }
          
          // Prefetch user data in background
          prefetchUserData(userUID, fetchedUserType);
          
          return fetchedUserType;
        }
        
        debug.log('AuthContext', `User type not found, ${retries - 1} retries left`, { userUID });
        retries--;
        
        if (retries > 0) {
          await new Promise(resolve => setTimeout(resolve, delayMs));
        }
      } catch (error) {
        debug.error('AuthContext', 'Error fetching user type', { userUID, error });
        retries--;
        
        if (retries > 0) {
          await new Promise(resolve => setTimeout(resolve, delayMs));
        }
      }
    }
    
    debug.error('AuthContext', 'Failed to fetch user type after all retries', { userUID });
    return null;
  };

  useEffect(() => {
    debug.component.mount('AuthContext');
    debug.firebase('AuthContext', 'Setting up auth state listener');
    
    const unsubscribe = onIdTokenChanged(auth, async (user) => {
      setLoading(true);
      setNavigationReady(false);
      debug.firebase('AuthContext', 'Auth state changed', { userUID: user?.uid });
      
      if (user) {
        // Set current user and verified status immediately
        // Critical: Setting these states together prevents race conditions
        setCurrentUser(user);
        setIsVerified(user.emailVerified);
        
        // Log verification status immediately for debugging
        debug.auth('AuthContext', 'User verification status', { 
          emailVerified: user.emailVerified,
          userUID: user.uid
        });
        
        try {
          // First check if we have a valid cached token before requesting a new one
          const { token: cachedToken, isValid } = getCachedAuthData();
          let token;
          
          if (isValid && cachedToken) {
            token = cachedToken;
            debug.log('AuthContext', 'Using cached token during auth state change');
            setAuthToken(token);
          } else {
            // Only force refresh if we don't have a valid cached token
            try {
              token = await getIdToken(user, false); // Use false to reduce quota usage
              setAuthToken(token);
              setLastRefreshTime();
            } catch (tokenError) {
              if (tokenError.code === 'auth/quota-exceeded' && isValid && cachedToken) {
                // Fall back to cached token if quota exceeded
                debug.warn('AuthContext', 'Quota exceeded, using cached token');
                token = cachedToken;
                setAuthToken(token);
              } else {
                throw tokenError;
              }
            }
          }
          
          // More robust handling for user type
          if (userType && user.uid === currentUser?.uid) {
            debug.log('AuthContext', 'Maintaining existing userType', { userType });
            cacheAuthData(token, userType);
          } else {
            // Check if we have a cached userType before attempting a fetch
            const { userType: cachedUserType, isValid } = getCachedAuthData();
            
            if (isValid && cachedUserType) {
              debug.log('AuthContext', 'Using cached userType', { userType: cachedUserType });
              setUserType(cachedUserType);
              cacheAuthData(token, cachedUserType);
            } else {
              debug.firebase('AuthContext', 'Fetching userType for user', { userUID: user.uid });
              const userTypeValue = await fetchUserTypeWithRetry(user.uid);
              setUserType(userTypeValue);
              cacheAuthData(token, userTypeValue);
            }
          }
        } catch (error) {
          debug.error('AuthContext', 'Error in auth state change handler', error);
          setUserType(null);
          setAuthToken(null);
          cacheAuthData(null, null);
        }
      } else {
        setCurrentUser(null);
        setUserType(null);
        setIsVerified(false);
        setAuthToken(null);
        cacheAuthData(null, null);
        debug.log('AuthContext', 'No user signed in');
      }
      
      setLoading(false);
      setAuthInitialized(true);
      
      setTimeout(() => {
        setNavigationReady(true);
        debug.log('AuthContext', 'Navigation readiness set to true');
      }, 300);
      
      debug.log('AuthContext', 'Auth initialization complete', {
        userUID: user?.uid,
        userType: userType,
        isVerified: user?.emailVerified,
        hasToken: !!authToken
      });
    });
    
    return () => {
      unsubscribe();
      debug.component.unmount('AuthContext');
    };
  }, [currentUser?.uid, userType, authToken]);

  useEffect(() => {
    if (!currentUser) return;
    
    checkAndRefreshToken();
    
    const intervalId = setInterval(() => {
      if (!currentUser) {
        clearInterval(intervalId); // Clear interval if no user
        return;
      }
      checkAndRefreshToken();
    }, TOKEN_REFRESH_INTERVAL);
    
    return () => {
      clearInterval(intervalId);
    };
  }, [currentUser, checkAndRefreshToken]);

  useEffect(() => {
    if (!currentUser) return;
    
    // Reduce the number of events that trigger token refreshes
    // Only listen for more deliberate actions, not continuous ones like scroll
    const activityEvents = ['mousedown', 'keydown'];
    
    // Add debounce to prevent multiple quick refreshes
    let activityTimer = null;
    const ACTIVITY_DEBOUNCE = 5 * 60 * 1000; // 5 minutes
    
    const handleUserActivity = () => {
      // Clear any existing timer
      if (activityTimer) {
        clearTimeout(activityTimer);
      }
      
      // Set a new timer for refresh
      activityTimer = setTimeout(() => {
        checkAndRefreshToken();
      }, ACTIVITY_DEBOUNCE);
    };
    
    activityEvents.forEach(event => {
      window.addEventListener(event, handleUserActivity, { passive: true });
    });
    
    return () => {
      if (activityTimer) {
        clearTimeout(activityTimer);
      }
      
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleUserActivity);
      });
    };
  }, [currentUser, checkAndRefreshToken]);

  const refreshUser = async () => {
    if (!currentUser) {
      debug.warn('AuthContext', 'Cannot refresh user - no current user');
      return null;
    }
    
    debug.firebase('AuthContext', 'Refreshing user state', { userUID: currentUser.uid });
    setNavigationReady(false);
    
    try {
      // Force reload the user to get latest verification status
      await currentUser.reload();
      
      // Check fresh verification status and log it for debugging
      const freshVerificationStatus = currentUser.emailVerified;
      debug.auth('AuthContext', 'Updated verification status', { 
        emailVerified: freshVerificationStatus,
        userUID: currentUser.uid
      });
      
      // Immediately update verified status to prevent race conditions
      setIsVerified(freshVerificationStatus);
      
      // Check if we can use cached token to avoid quota issues
      let token;
      const now = Date.now();
      const { token: cachedToken, isValid } = getCachedAuthData();
      const lastRefreshTime = getLastRefreshTime();
      
      if (isValid && cachedToken && now - lastRefreshTime < MIN_REFRESH_INTERVAL) {
        debug.log('AuthContext', 'Using cached token for refreshUser (rate limited)');
        token = cachedToken;
      } else {
        try {
          token = await getIdToken(currentUser, true);
          setLastRefreshTime();
        } catch (tokenError) {
          if (tokenError.code === 'auth/quota-exceeded' && isValid && cachedToken) {
            debug.warn('AuthContext', 'Auth quota exceeded in refreshUser, using cached token');
            token = cachedToken;
          } else {
            throw tokenError;
          }
        }
      }
      
      setAuthToken(token);
      
      const userTypeValue = await fetchUserTypeWithRetry(currentUser.uid);
      if (userTypeValue) {
        setUserType(userTypeValue);
        cacheAuthData(token, userTypeValue);
        debug.firebase('AuthContext', 'Refreshed user type', { userType: userTypeValue });
      } else {
        debug.warn('AuthContext', 'Could not refresh user type - not found after retries', { userUID: currentUser.uid });
      }
      
      setTimeout(() => {
        setNavigationReady(true);
        debug.log('AuthContext', 'Navigation readiness set to true after refresh');
      }, 300);
      
      return {
        userUID: currentUser.uid,
        userType: userTypeValue,
        isVerified: currentUser.emailVerified,
        token
      };
    } catch (error) {
      debug.error('AuthContext', 'Error refreshing user', { userUID: currentUser.uid, error });
      
      setTimeout(() => {
        setNavigationReady(true);
        debug.log('AuthContext', 'Navigation readiness set to true after refresh error');
      }, 300);
      
      return null;
    }
  };

  // Memoize callback functions separately to optimize React renders
  const refreshTokenMemo = useCallback(refreshToken, [currentUser, shouldRefreshToken]);
  const refreshUserMemo = useCallback(refreshUser, [currentUser]);
  const initializeAuthMemo = useCallback(initializeAuth, []);

  const value = useMemo(() => ({
    currentUser,
    isVerified,
    userType,
    authToken,
    authReady: !loading && authInitialized,
    navigationReady,
    loading,
    initializeAuth: initializeAuthMemo,
    refreshUser: refreshUserMemo,
    refreshToken: refreshTokenMemo,
    isAdmin: userType === 'Admin'
  }), [
    currentUser,
    isVerified,
    userType,
    authToken,
    loading,
    authInitialized,
    navigationReady,
    initializeAuthMemo,
    refreshUserMemo,
    refreshTokenMemo,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;